import { useActivities, useActivity } from '@/api/activities';
import { ApiError } from '@/api/client';
import { useCreateLesson } from '@/api/lessons';
import { useMasterLists, useSharedMasterLists } from '@/api/masterLists';
import { useMe } from '@/api/me';
import { usePreference } from '@/api/preferences';
import {
  useAddTripItem,
  useAddTripItemLabel,
  useAssignTripItems,
  useBulkUpdateTripItems,
  useCancelTripInvite,
  useCloseTrip,
  useCreateTripInvite,
  useCreateTripItemComment,
  useDeleteTripCollaborator,
  useDeleteTripItem,
  useDeleteTripItemComment,
  useDeleteTripItemLabel,
  useLinkTripItems,
  useMarkTripItemBought,
  usePromoteTripList,
  usePullIntoTrip,
  useRemoveTripList,
  useRemovedTripItems,
  useReopenTrip,
  useReorderTripItems,
  useRestoreTripItem,
  useTrip,
  useTripCollaborators,
  useTripEvents,
  useTripInvites,
  useTripItemComments,
  useTripItems,
  useTripLabels,
  useTripMembers,
  useTripQuestions,
  useUnlinkTripItem,
  useUpdateTrip,
  useUpdateTripCollaborator,
  useUpdateTripItem,
  useUpdateTripItemComment,
  useUpdateTripItemFlag,
  useUpdateTripItemQuestion,
  useUpdateTripItemStatus,
} from '@/api/trips';
import { Badge } from '@/components/library/ui/Badge';
import { ConfirmDialog } from '@/components/library/ui/ConfirmDialog';
import { Skeleton } from '@/components/library/ui/Skeleton';
import { Tabs } from '@/components/library/ui/Tabs';
import { CopyTripPromptButton, useCopyTripPrompt } from '@/components/trips/CopyTripPromptButton';
import { FindLinkedItemsDialog } from '@/components/trips/FindLinkedItemsDialog';
import {
  NewTripListDialog,
  RenameTripListDialog,
  type TripListMoveResult,
} from '@/components/trips/NewTripListDialog';
import { PersonInitial } from '@/components/trips/PersonInitial';
import { QuickAddModal } from '@/components/trips/QuickAddModal';
import { ReconcileDialog } from '@/components/trips/ReconcileDialog';
import { TripCheckoff } from '@/components/trips/TripCheckoff';
import { TripItemActivity } from '@/components/trips/TripItemActivity';
import {
  TripItemEventDetails,
  describeTripItemEvent,
  tripItemEventTimeAgo,
} from '@/components/trips/TripItemActivity';
import { type WhoFilter, WhoFilterMenu } from '@/components/trips/WhoFilterMenu';
import { colorByMember, personName } from '@/components/trips/people';
import { type TripItemGroup, groupTripItemsByOrigin } from '@/components/trips/tripGrouping';
import { useTripCommands } from '@/components/trips/useTripCommands';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import type { StreamStatus } from '@/lib/tripStream';
import { useDocumentTitle } from '@/lib/useDocumentTitle';
import { type FieldDraft, useFieldDraft } from '@/lib/useFieldDraft';
import { applyRemoteChanges } from '@/lib/useRemoteTripChanges';
import { isTypingTarget, overlayOpen, useTripModeShortcut } from '@/lib/useTripModeShortcut';
import { useTripSync } from '@/lib/useTripSync';
import { cn } from '@/lib/utils';
import { useQuickAddStore } from '@/stores/quickAdd';
import { changedItemIds, useTripChangesStore } from '@/stores/tripChanges';
import { useTripModeStore } from '@/stores/tripMode';
import { useTripPresence } from '@/stores/tripPresence';
import { useTripSelection } from '@/stores/tripSelection';
import { useTripViewPrefs } from '@/stores/tripViewPrefs';
import {
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import type {
  Activity,
  MasterList,
  PresenceEntry,
  Trip,
  TripCollaboratorWithUser,
  TripItemComment,
  TripItemEvent,
  TripItemWithLabels,
  TripMember,
  UserSummary,
} from '@ultimate-checklist/shared';
import {
  BUILTIN_LABELS,
  LIST_WIDTH_MAX,
  LIST_WIDTH_MIN,
  ShareRole,
  TripItemConflict,
  tripListKey,
} from '@ultimate-checklist/shared';
import {
  Archive,
  Check,
  CheckCircle2,
  CheckSquare2,
  ChevronDown,
  Circle,
  ClipboardList,
  Flag,
  GripVertical,
  Lightbulb,
  Link2,
  ListPlus,
  MoreHorizontal,
  PackagePlus,
  Pencil,
  Plus,
  Printer,
  RotateCcw,
  Search,
  Settings2,
  ShoppingCart,
  Sparkles,
  Square,
  SquareCheck,
  Tag,
  Trash2,
  Unlink,
  Users,
  X,
} from 'lucide-react';
import {
  type FormEvent,
  type KeyboardEvent,
  Suspense,
  lazy,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// Lazy: pulls in @react-pdf/renderer (~0.5MB gz) only when the user opens Print.
const PrintBoardDialog = lazy(() => import('@/components/trips/print/PrintBoardDialog'));
import { toast } from 'sonner';

type TripDragData =
  | { type: 'activity'; activityId: string }
  | { type: 'master-list'; masterListId: string }
  | { type: 'trip-item'; itemId: string }
  | { type: 'trip-drop-zone' };

/** Trip-scoped view: the calm Overview (default) or the plan/pack surface. `list`
 * carries the drill-down target from an Overview card into the plan view. */
export type TripView = 'overview' | 'plan';
export type TripItemTab = 'details' | 'activity' | 'lessons';
/** Item filter for the overview. `undefined` = All. */
export type TripFilter = 'unchecked' | 'checked' | 'flagged' | 'changed';
const TRIP_FILTERS: readonly TripFilter[] = ['unchecked', 'checked', 'flagged', 'changed'];
/** Empty-state copy when a filter matches nothing. */
const TRIP_FILTER_EMPTY: Record<TripFilter, { title: string; description: string }> = {
  unchecked: {
    title: 'Nothing left to pack',
    description: 'Every item here is checked off. Nice work.',
  },
  checked: {
    title: 'Nothing packed yet',
    description: 'Items you check off will show up here.',
  },
  flagged: {
    title: 'No flagged items',
    description: 'Flag items that need extra attention and they’ll show up here.',
  },
  changed: {
    title: 'No changed items',
    description: 'Items from the last batch of applied changes show here until you clear them.',
  },
};
export interface TripSearch {
  view: TripView;
  filter?: TripFilter;
  /** Handler filter: 'me', 'none' (unassigned), or a member id. Absent = everyone. */
  who?: string;
  /** Quick filter text (matches item names, list names, labels). */
  q?: string;
  list?: string;
  item?: string;
  itemTab?: TripItemTab;
  /** Origin master list id of the trip list being reconciled (opens the audit). */
  reconcile?: string;
  /** Opens the cross-list "find linked items" AI scan dialog. */
  findLinked?: boolean;
}

export const Route = createFileRoute('/trips/$tripId')({
  validateSearch: (search: Record<string, unknown>): TripSearch => ({
    view: search.view === 'plan' ? 'plan' : 'overview',
    filter: TRIP_FILTERS.includes(search.filter as TripFilter)
      ? (search.filter as TripFilter)
      : undefined,
    who: typeof search.who === 'string' && search.who !== 'all' ? search.who : undefined,
    q: typeof search.q === 'string' && search.q.trim() ? search.q : undefined,
    list: typeof search.list === 'string' ? search.list : undefined,
    item: typeof search.item === 'string' ? search.item : undefined,
    reconcile: typeof search.reconcile === 'string' ? search.reconcile : undefined,
    findLinked: search.findLinked === true || search.findLinked === 'true' ? true : undefined,
    itemTab:
      search.itemTab === 'activity' || search.itemTab === 'lessons' || search.itemTab === 'details'
        ? search.itemTab
        : undefined,
  }),
  component: TripDetail,
});

function userDisplayName(user: UserSummary | null): string {
  return user?.displayName?.trim() || user?.email || 'Unknown user';
}

function commentTimestamp(value: string): string {
  return new Date(value).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function shouldSubmitCommentWithEnter(event: KeyboardEvent<HTMLTextAreaElement>): boolean {
  if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) {
    return false;
  }
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

function TripDetail() {
  const { tripId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const trip = useTrip(tripId);
  const items = useTripItems(tripId);
  const labels = useTripLabels(tripId);
  const me = useMe();
  useDocumentTitle(trip.data?.name);
  // Other people's edits: streamed live (and applied as they happen) when live
  // collaboration is on, otherwise detected by the poll and held until applied.
  const live = useTripSync(tripId);
  const liveEnabled = usePreference('liveCollaboration');
  const liveStatus = useTripPresence((state) => state.status);
  const setMine = useTripPresence((state) => state.setMine);
  const clearRemoved = useTripPresence((state) => state.clearRemoved);
  const restoreRemoved = useRestoreTripItem(tripId);
  // E cycles View → Edit → Edit all; Escape back to View.
  useTripModeShortcut();
  const [manageOpen, setManageOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [listReorderMode, setListReorderMode] = useState(false);
  // On-the-fly lists: create dialog, the local list pending Push to Library, and
  // the local list being renamed.
  const [newListOpen, setNewListOpen] = useState(false);
  const [promoteTarget, setPromoteTarget] = useState<string | null>(null);
  const [renameTarget, setRenameTarget] = useState<TripItemGroup | null>(null);
  const promoteList = usePromoteTripList(tripId);
  const myMasterLists = useMasterLists();
  const setScrollTargetKey = useQuickAddStore((s) => s.setScrollTargetKey);
  // While the Manage "List width" slider is dragging, this overrides the persisted
  // width so the masonry previews live without writing the cache.
  const [previewListWidth, setPreviewListWidth] = useState<number | null>(null);
  const notFound = trip.error instanceof ApiError && trip.error.code === 'NOT_FOUND';
  const isOwner = Boolean(trip.data && me.data?.id === trip.data.ownerId);
  const openItemId = search.item ?? null;
  const openItem = items.data?.find((item) => item.id === openItemId) ?? null;
  const activeItemTab = search.itemTab ?? 'details';
  // Live mode: someone else removed the item this sheet shows — who, for the tombstone.
  const removedBy = useTripPresence((state) =>
    openItemId ? state.removedByOthers[openItemId] : undefined,
  );
  // Presence: tell other live tabs which item this one has open.
  useEffect(() => {
    setMine({ openItemId });
    return () => setMine({ openItemId: null });
  }, [openItemId, setMine]);
  // The trip list (origin group) being reconciled, if any — resolved to its
  // master list id + name for the audit dialog.
  const reconcileGroup = search.reconcile
    ? groupTripItemsByOrigin(items.data ?? []).find((group) => group.originId === search.reconcile)
    : undefined;
  const reconcileTarget = reconcileGroup?.originId
    ? { originId: reconcileGroup.originId, name: reconcileGroup.name }
    : undefined;
  // Quick filter: the box is open while typing or while a query is active.
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const query = search.q?.trim().toLocaleLowerCase() ?? '';
  // What the text applies to: item names (+ labels), list names, or both.
  const filterScope = useTripViewPrefs((state) => state.filterScope);
  const setFilterScope = useTripViewPrefs((state) => state.setFilterScope);
  const matchesQuery = (item: TripItemWithLabels): boolean => {
    if (!query) {
      return true;
    }
    const listName = (item.originMasterListName ?? 'Manually added items').toLocaleLowerCase();
    const itemHit =
      filterScope.items &&
      (item.name.toLocaleLowerCase().includes(query) ||
        item.labels.some((label) => label.toLocaleLowerCase().includes(query)));
    const listHit = filterScope.lists && listName.includes(query);
    return itemHit || listHit;
  };
  // Reordering ignores the filters (you reorder the whole list); otherwise narrow
  // items to the status filter AND the handler filter AND the quick filter.
  const who = search.who;
  const matchesWho = (item: TripItemWithLabels): boolean => {
    if (!who) {
      return true;
    }
    if (who === 'me') {
      return item.handledBy !== null && item.handledBy === me.data?.id;
    }
    if (who === 'none') {
      return item.handledBy === null;
    }
    return item.handledBy === who;
  };
  // "Changed": the items touched by the last batch of applied remote changes —
  // the same set that carries the highlight on the board, until it's cleared.
  const appliedRemote = useTripChangesStore((state) => state.applied);
  const appliedItemIds = useMemo(() => changedItemIds(appliedRemote), [appliedRemote]);
  const hasChanged = appliedRemote.length > 0;
  const matchesStatus = (item: TripItemWithLabels): boolean => {
    switch (search.filter) {
      case 'flagged':
        return item.flagged;
      case 'unchecked':
        return item.status !== 'done';
      case 'checked':
        return item.status === 'done';
      case 'changed':
        return appliedItemIds.has(item.id);
      default:
        return true;
    }
  };
  const filteredItems =
    listReorderMode || (!search.filter && !who && !query)
      ? items.data
      : items.data?.filter((item) => matchesStatus(item) && matchesWho(item) && matchesQuery(item));
  const members = useTripMembers(tripId);
  const whoMember =
    who && who !== 'me' && who !== 'none' ? members.data?.find((m) => m.id === who) : undefined;
  // Empty-state copy: the quick filter wins, then the handler filter, then status.
  const filterEmpty = query
    ? {
        title: `Nothing matches “${search.q?.trim()}”`,
        description: 'Try a shorter word, or clear the other filters.',
      }
    : who
      ? {
          title:
            who === 'me'
              ? 'Nothing assigned to you'
              : who === 'none'
                ? 'Nothing unassigned'
                : `Nothing for ${whoMember ? personName(whoMember) : 'this person'}`,
          description: search.filter
            ? 'Nothing matches both filters. Try clearing one.'
            : 'Assign items from an item’s details, or a whole list from its options.',
        }
      : search.filter
        ? TRIP_FILTER_EMPTY[search.filter]
        : undefined;

  // Open each trip in the safe View mode so items can't be checked by accident.
  const setTripMode = useTripModeStore((state) => state.setMode);
  const setEditAll = useTripModeStore((state) => state.setEditAll);
  const startSelection = useTripSelection((state) => state.start);
  const stopSelection = useTripSelection((state) => state.stop);
  // Leaving the trip ends any selection.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset when the trip changes
  useEffect(() => () => stopSelection(), [stopSelection, tripId]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset when the trip changes
  useEffect(() => setTripMode('view'), [setTripMode, tripId]);

  // These navigations only flip UI state stored in the URL (the item sheet, its
  // tab, dialogs, the filter) — they stay on the same page, so `resetScroll: false`
  // keeps the window where it is instead of jumping to the top on each change.
  function openItemSheet(item: TripItemWithLabels) {
    void navigate({
      resetScroll: false,
      search: (current) => ({
        ...current,
        item: item.id,
        itemTab: 'details',
      }),
    });
  }

  function closeItemSheet() {
    void navigate({
      resetScroll: false,
      search: (current) => ({
        ...current,
        item: undefined,
        itemTab: undefined,
      }),
    });
  }

  function setItemTab(itemTab: TripItemTab) {
    void navigate({
      resetScroll: false,
      search: (current) => ({
        ...current,
        itemTab,
      }),
    });
  }

  function setReconcile(originMasterListId: string | undefined) {
    void navigate({
      resetScroll: false,
      search: (current) => ({ ...current, reconcile: originMasterListId }),
    });
  }

  function setFindLinked(open: boolean) {
    void navigate({
      resetScroll: false,
      search: (current) => ({ ...current, findLinked: open ? true : undefined }),
    });
  }

  function setFilter(filter: TripFilter | undefined) {
    if (listReorderMode) {
      return;
    }
    void navigate({
      resetScroll: false,
      search: (current) => ({
        ...current,
        filter,
      }),
    });
  }

  function setQuery(next: string) {
    // Replace, not push: one history entry per keystroke would bury the back button.
    void navigate({
      resetScroll: false,
      replace: true,
      search: (current) => ({ ...current, q: next.trim() ? next : undefined }),
    });
  }

  function openSearch() {
    if (listReorderMode) {
      return;
    }
    setSearchOpen(true);
    // The input mounts on the next render; focus it once it's there, and make
    // sure the filter row is on screen (the header may be scrolled away).
    requestAnimationFrame(() => {
      searchInputRef.current?.focus();
      searchInputRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  }

  function closeSearch() {
    setSearchOpen(false);
    setQuery('');
  }

  // The "Changed" filter only exists while a batch is highlighted; when it's
  // cleared (or on a fresh load) fall back to All rather than an empty board.
  useEffect(() => {
    if (search.filter === 'changed' && !hasChanged && !listReorderMode) {
      void navigate({
        resetScroll: false,
        search: (current) => ({ ...current, filter: undefined }),
      });
    }
  }, [search.filter, hasChanged, listReorderMode, navigate]);

  // Keyboard: "/" focuses the quick filter; "A" opens Quick add (voice, like the
  // header mic — a key press counts as the user gesture the mic needs); "F"
  // toggles the flag on the row under the mouse (rows carry data-item-id), with
  // the red bar as the feedback. Same guards as E: not while typing, not with an
  // overlay open.
  const updateFlag = useUpdateTripItemFlag(tripId);
  useEffect(() => {
    // `KeyboardEvent` here is React's (imported for the comment box); use the DOM one.
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      const isSearchKey = event.key === '/';
      const isAddKey = event.key === 'a' || event.key === 'A';
      const isFlagKey = event.key === 'f' || event.key === 'F';
      if (!isSearchKey && !isAddKey && !isFlagKey) {
        return;
      }
      if (isTypingTarget(event.target) || overlayOpen()) {
        return;
      }
      if (isSearchKey) {
        event.preventDefault();
        openSearch();
        return;
      }
      if (isAddKey) {
        // The floating panel isn't a modal, so guard against re-launching over itself.
        if (listReorderMode || useQuickAddStore.getState().open) {
          return;
        }
        event.preventDefault();
        useQuickAddStore.getState().launchVoice();
        return;
      }
      if (listReorderMode) {
        return;
      }
      const hovered = document.querySelector<HTMLElement>('[data-item-id]:hover');
      const itemId = hovered?.dataset.itemId;
      const item = itemId ? items.data?.find((candidate) => candidate.id === itemId) : undefined;
      if (!item) {
        return; // nothing under the pointer — let the key fall through
      }
      event.preventDefault();
      updateFlag.mutate({ itemId: item.id, body: { flagged: !item.flagged } });
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  function setWho(next: WhoFilter) {
    if (listReorderMode) {
      return;
    }
    void navigate({
      resetScroll: false,
      search: (current) => ({ ...current, who: next === 'all' ? undefined : next }),
    });
  }

  function startListReorder() {
    setListReorderMode(true);
    setEditAll(false);
    setManageOpen(false);
    if (search.filter) {
      setFilter(undefined);
    }
  }

  // Command palette entries for this trip (contextual: on-screen lists rank first).
  const qc = useQueryClient();
  const copyPrompt = useCopyTripPrompt(
    trip.data ?? {
      id: tripId,
      ownerId: '',
      name: '',
      status: 'active',
      closedAt: null,
      listWidth: 480,
      liveCollaboration: false,
      createdAt: '',
      updatedAt: '',
    },
  );
  useTripCommands(trip.data, {
    openItemSheet,
    setFilter,
    setWho,
    openSearch,
    openManage: () => setManageOpen(true),
    openPrint: () => setPrintOpen(true),
    openNewList: () => setNewListOpen(true),
    startListReorder,
    copyForAi: copyPrompt.copy,
    applyChanges: () => void applyRemoteChanges(qc),
  });

  function onListMoveDone(result: TripListMoveResult) {
    setManageOpen(false);
    setScrollTargetKey(result.key);
    toast.success(
      result.created
        ? `Created “${result.name}”`
        : `Moved ${result.movedCount} ${result.movedCount === 1 ? 'item' : 'items'} to “${result.name}”`,
      { position: 'bottom-center' },
    );
  }

  async function confirmPromote() {
    if (!promoteTarget || promoteList.isPending) {
      return;
    }
    try {
      const result = await promoteList.mutateAsync({ originMasterListName: promoteTarget });
      setPromoteTarget(null);
      toast.success(`“${result.masterList.name}” is now in your Library`, {
        position: 'bottom-center',
      });
    } catch {
      toast.error('Couldn’t push that list to the Library');
    }
  }

  function renderFilterControls(className?: string) {
    const searching = searchOpen || Boolean(query);
    // Phones: the search box takes the whole row (pills hide). md+: the pills
    // and Who stay put and the box simply replaces the magnifier.
    const scopeToggles = (
      [
        { key: 'items', label: 'Items' },
        { key: 'lists', label: 'Lists' },
      ] as const
    ).map(({ key, label }) => {
      const on = filterScope[key];
      const other = key === 'items' ? filterScope.lists : filterScope.items;
      return (
        <button
          key={key}
          type="button"
          aria-pressed={on}
          disabled={on && !other}
          title={`Match ${label.toLowerCase()}`}
          onMouseDown={(event) => event.preventDefault()} // keep focus in the box
          onClick={() => setFilterScope({ ...filterScope, [key]: !on })}
          className={cn(
            'inline-flex h-8 shrink-0 items-center gap-1 rounded-md px-2 font-medium text-xs transition-colors',
            on
              ? 'bg-[var(--primary)] text-[var(--primary-foreground)]'
              : 'text-[var(--muted-foreground)] hover:bg-[var(--accent)] hover:text-[var(--accent-foreground)]',
            'disabled:opacity-100',
          )}
        >
          {on ? <Check className="h-3 w-3" /> : null}
          {label}
        </button>
      );
    });
    return (
      <div
        className={cn(
          'inline-grid rounded-md border border-[var(--border)] bg-[var(--card)] p-1',
          // Written out per pill count so Tailwind sees every variant.
          searching
            ? hasChanged
              ? 'grid-cols-1 md:grid-cols-[repeat(5,1fr)_auto_minmax(22rem,auto)]'
              : 'grid-cols-1 md:grid-cols-[repeat(4,1fr)_auto_minmax(22rem,auto)]'
            : hasChanged
              ? 'grid-cols-[auto_auto_auto_auto_auto_auto_auto] md:grid-cols-[repeat(5,1fr)_auto_auto]'
              : 'grid-cols-[auto_auto_auto_auto_auto_auto] md:grid-cols-[repeat(4,1fr)_auto_auto]',
          className,
        )}
      >
        {/* Phones: icons (the same vocabulary as the rows) so all six controls fit
            without scrolling; md+: words. `All` stays a word at every size. */}
        {(
          [
            { value: undefined, label: 'All', icon: null },
            { value: 'unchecked', label: 'Unchecked', icon: Square },
            { value: 'checked', label: 'Checked', icon: SquareCheck },
            { value: 'flagged', label: 'Flagged', icon: Flag },
            // Only while a batch of applied changes is highlighted on the board.
            ...(hasChanged
              ? [{ value: 'changed' as const, label: 'Changed', icon: Sparkles }]
              : []),
          ] as const
        ).map((segment) => {
          const active = (search.filter ?? undefined) === segment.value;
          const isFlagged = segment.value === 'flagged';
          const Icon = segment.icon;
          return (
            <Button
              key={segment.label}
              type="button"
              size="sm"
              variant={active ? 'default' : 'ghost'}
              className={cn(
                'px-2 md:px-3',
                searching && 'max-md:hidden',
                active && isFlagged && 'bg-red-600 text-white hover:bg-red-700',
              )}
              aria-pressed={active}
              aria-label={segment.label}
              title={segment.label}
              disabled={listReorderMode}
              onClick={() => setFilter(segment.value)}
            >
              {Icon ? <Icon className={cn(isFlagged && active && 'fill-white')} /> : null}
              <span className={Icon ? 'hidden md:inline' : undefined}>{segment.label}</span>
            </Button>
          );
        })}
        <div className={cn(searching && 'max-md:hidden')}>
          <WhoFilterMenu
            tripId={tripId}
            value={search.who ?? 'all'}
            onChange={setWho}
            disabled={listReorderMode}
          />
        </div>
        {searching ? (
          <div className="flex min-w-0 items-center gap-1 md:border-[var(--border)] md:border-l md:pl-1">
            <Search className="ml-2 h-4 w-4 shrink-0 text-[var(--muted-foreground)]" />
            <input
              ref={searchInputRef}
              id="trip-search"
              type="search"
              value={search.q ?? ''}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  event.preventDefault();
                  closeSearch();
                }
              }}
              onBlur={() => {
                if (!query) {
                  setSearchOpen(false);
                }
              }}
              placeholder={
                filterScope.items && filterScope.lists
                  ? 'Filter items and lists…'
                  : filterScope.items
                    ? 'Filter items…'
                    : 'Filter lists…'
              }
              aria-label="Quick filter"
              enterKeyHint="search"
              autoCapitalize="none"
              autoCorrect="off"
              className="h-9 min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-[var(--muted-foreground)] md:h-8 md:text-sm"
            />
            {scopeToggles}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              aria-label="Clear filter"
              onClick={closeSearch}
            >
              <X />
            </Button>
          </div>
        ) : (
          <button
            type="button"
            aria-label="Quick filter (/)"
            title="Quick filter (/)"
            disabled={listReorderMode}
            onClick={openSearch}
            className="inline-flex h-10 w-11 items-center justify-center rounded-md text-[var(--muted-foreground)] transition-colors hover:bg-[var(--accent)] hover:text-[var(--accent-foreground)] disabled:pointer-events-none disabled:opacity-50 md:h-8 md:w-10"
          >
            <Search className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-[3264px] flex-col gap-6">
      {trip.isLoading ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-9 w-1/2" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : notFound ? (
        <div className="flex flex-col items-center gap-4 py-16 text-center">
          <h2 className="text-lg font-semibold">Trip not found</h2>
          <p className="max-w-sm text-sm text-[var(--muted-foreground)]">
            This trip doesn’t exist or isn’t available to you.
          </p>
          <Button asChild variant="outline">
            <Link to="/trips">Back to Trips</Link>
          </Button>
        </div>
      ) : trip.isError || !trip.data ? (
        <div className="flex flex-col items-center gap-4 py-16 text-center">
          <h2 className="text-lg font-semibold">Couldn’t load this trip</h2>
          <Button asChild variant="outline">
            <Link to="/trips">Back to Trips</Link>
          </Button>
        </div>
      ) : (
        <>
          {/* Clean header: name + Manage. Trip progress lives in the sticky app header. */}
          <header className="flex flex-col gap-3">
            <div className="flex items-start gap-3 md:hidden">
              <h1 className="flex min-w-0 flex-1 items-center gap-2 truncate text-2xl font-semibold">
                <span className="min-w-0 truncate">{trip.data.name}</span>
                {live ? <LiveDot status={liveStatus} /> : null}
              </h1>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setNewListOpen(true)}
                className="shrink-0"
                aria-label="New list"
              >
                <ListPlus />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setManageOpen(true)}
                className="shrink-0"
                aria-label="Manage trip"
              >
                <Settings2 />
              </Button>
            </div>
            {renderFilterControls('w-full md:hidden')}

            <div className="hidden flex-wrap items-start gap-3 md:flex">
              <h1 className="flex min-w-0 max-w-full items-center gap-2 truncate text-2xl font-semibold">
                <span className="min-w-0 truncate">{trip.data.name}</span>
                {live ? <LiveDot status={liveStatus} /> : null}
              </h1>
              {renderFilterControls('md:justify-self-center')}
              <Button
                variant="outline"
                onClick={() => setNewListOpen(true)}
                className="ml-auto shrink-0"
                aria-label="New list"
              >
                <ListPlus />
                <span className="hidden sm:inline">New list</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => setManageOpen(true)}
                className="shrink-0"
                aria-label="Manage trip"
              >
                <Settings2 />
                <span className="hidden sm:inline">Manage</span>
              </Button>
            </div>
          </header>

          <TripCheckoff
            tripId={tripId}
            items={filteredItems}
            allItems={items.data}
            listWidth={previewListWidth ?? trip.data.listWidth}
            isLoading={items.isLoading}
            isError={items.isError}
            onOpenItem={openItemSheet}
            onManage={() => setManageOpen(true)}
            onReconcile={setReconcile}
            onPromote={setPromoteTarget}
            onRename={setRenameTarget}
            listReorderMode={listReorderMode}
            onExitListReorder={() => setListReorderMode(false)}
            emptyTitle={filterEmpty?.title}
            emptyDescription={filterEmpty?.description}
          />

          {/* AI audit — diff a trip list against its source library list. */}
          {reconcileTarget ? (
            <ReconcileDialog
              tripId={tripId}
              listId={reconcileTarget.originId}
              listName={reconcileTarget.name}
              open={true}
              onOpenChange={(next) => {
                if (!next) {
                  setReconcile(undefined);
                }
              }}
            />
          ) : null}

          {/* On-the-fly list: name it + move items in from the trip's other lists. */}
          <NewTripListDialog
            tripId={tripId}
            open={newListOpen}
            onOpenChange={setNewListOpen}
            onDone={onListMoveDone}
          />

          {/* Push a trip-local list up to the Library (then audit works on it). */}
          <ConfirmDialog
            open={promoteTarget !== null}
            onOpenChange={(open) => !open && setPromoteTarget(null)}
            title={`Push “${promoteTarget ?? ''}” to your Library?`}
            description={
              promoteTarget
                ? `This creates a master list in your Library with this list's items and links them, so you can audit the trip against it and reuse it on future trips.${
                    myMasterLists.data?.some(
                      (list) => list.name.toLowerCase() === promoteTarget.toLowerCase(),
                    )
                      ? ' Heads up: your Library already has a list with this name — you’ll end up with two.'
                      : ''
                  }`
                : ''
            }
            confirmLabel="Push to Library"
            pending={promoteList.isPending}
            onConfirm={() => void confirmPromote()}
          />

          {/* Rename a trip-local list (whole-group move to the new name). */}
          <RenameTripListDialog
            tripId={tripId}
            group={renameTarget}
            onOpenChange={(open) => !open && setRenameTarget(null)}
          />

          {/* AI scan — find the same item across this trip's different lists. */}
          {search.findLinked ? (
            <FindLinkedItemsDialog
              tripId={tripId}
              open={true}
              onOpenChange={(next) => {
                if (!next) {
                  setFindLinked(false);
                }
              }}
            />
          ) : null}

          {/* Print studio — lazy (loads @react-pdf/renderer only on demand). */}
          {printOpen ? (
            <Suspense fallback={null}>
              <PrintBoardDialog
                trip={trip.data}
                items={items.data ?? []}
                onClose={() => setPrintOpen(false)}
              />
            </Suspense>
          ) : null}

          {/* Item detail — full-height slide-out from the right. */}
          <Sheet
            open={openItemId !== null}
            onOpenChange={(next) => {
              if (!next) {
                closeItemSheet();
              }
            }}
          >
            <SheetContent
              side="right"
              className="w-screen max-w-none gap-0 p-0 sm:w-[32rem]"
              // Don't restore focus to the opener row on close — when the item was
              // just deleted that row is gone, and the browser scroll-into-view on
              // focus restore makes the page jump.
              onCloseAutoFocus={(event) => event.preventDefault()}
            >
              <SheetTitle className="sr-only">Item</SheetTitle>
              {openItem ? (
                <TripItemSheetBody
                  tripId={tripId}
                  item={openItem}
                  labels={labels.data ?? []}
                  tab={activeItemTab}
                  onTabChange={setItemTab}
                  onDeleted={closeItemSheet}
                />
              ) : openItemId && removedBy !== undefined ? (
                <RemovedItemState
                  actorName={removedBy}
                  onRestore={() => {
                    restoreRemoved.mutate(openItemId);
                    clearRemoved(openItemId);
                  }}
                  onClose={closeItemSheet}
                />
              ) : openItemId ? (
                <div className="flex min-h-full flex-col gap-3 p-4 pr-16">
                  <Skeleton className="h-9 w-56" />
                  <Skeleton className="h-96 w-full" />
                </div>
              ) : null}
            </SheetContent>
          </Sheet>

          {/* Manage — rename, collaborators, add lists. Out of the way by default. */}
          <Sheet open={manageOpen} onOpenChange={setManageOpen}>
            <SheetContent
              side="right"
              className="w-screen max-w-none gap-0 p-0 sm:w-[56rem] sm:max-w-[calc(100vw-2rem)]"
              // Don't pull focus into the trip-name field on open — on mobile that
              // pops the keyboard and selects the text, which feels wrong.
              onOpenAutoFocus={(event) => event.preventDefault()}
            >
              <SheetTitle className="px-4 pt-4 pr-14 font-display text-lg font-semibold sm:px-6 sm:pt-6">
                Manage trip
              </SheetTitle>
              <div className="flex flex-col gap-4 p-4 pt-3 sm:p-6 sm:pt-4">
                <TripHeader trip={trip.data} />
                <TripDisplayPanel trip={trip.data} onPreview={setPreviewListWidth} />
                {liveEnabled ? <TripLivePanel trip={trip.data} /> : null}
                {isOwner ? <TripCollaboratorsPanel tripId={tripId} /> : null}
                <GuidedPullIn tripId={tripId} />
                <TripListsPanel
                  trip={trip.data}
                  listReorderMode={listReorderMode}
                  onStartListReorder={startListReorder}
                  onStopListReorder={() => setListReorderMode(false)}
                  onReconcile={(originId) => {
                    setManageOpen(false);
                    setReconcile(originId);
                  }}
                  onFindLinked={() => {
                    setManageOpen(false);
                    setFindLinked(true);
                  }}
                  onPrint={() => {
                    setManageOpen(false);
                    setPrintOpen(true);
                  }}
                  onPromote={setPromoteTarget}
                  onNewList={() => setNewListOpen(true)}
                  onSelectAcross={() => {
                    setManageOpen(false);
                    startSelection({ kind: 'trip' });
                  }}
                />
                <TripWideActionsPanel tripId={tripId} />
                <TripRemovedItemsPanel tripId={tripId} />
                <TripActivityPanel tripId={tripId} />
                <TripDangerZone trip={trip.data} />
              </div>
            </SheetContent>
          </Sheet>

          {/* AI Quick Add — launched from the route-aware button in the global nav. */}
          <QuickAddModal tripId={tripId} />
        </>
      )}
    </div>
  );
}

function TripHeader({ trip }: { trip: Trip }) {
  const [name, setName] = useState(trip.name);
  const updateTrip = useUpdateTrip(trip.id);

  useEffect(() => setName(trip.name), [trip.name]);

  // Auto-save on blur (and on Enter): commit only when the trimmed name changed
  // and isn't empty; revert otherwise. Hook owns optimistic rollback + error toast.
  function commitName() {
    const next = name.trim();
    if (next === trip.name) {
      return;
    }
    if (next.length === 0) {
      setName(trip.name);
      return;
    }
    updateTrip.mutate({ name: next });
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        (event.currentTarget.querySelector('#trip-title') as HTMLInputElement | null)?.blur();
      }}
      className="flex flex-col gap-2"
    >
      <Label htmlFor="trip-title" className="text-sm font-medium">
        Trip name
      </Label>
      <div className="flex gap-2">
        <Input
          id="trip-title"
          value={name}
          onChange={(event) => setName(event.target.value)}
          onBlur={commitName}
          className="font-semibold text-black dark:text-white"
        />
      </div>
      <p className="text-sm text-[var(--muted-foreground)]">
        {trip.status === 'closed' && trip.closedAt
          ? `Archived ${formatDate(trip.closedAt)}`
          : `Active · updated ${formatDate(trip.updatedAt)}`}
      </p>
    </form>
  );
}

/** Per-trip display: the list-card column width that drives how many columns fit.
 * The slider previews live via parent state (no cache write, so optimistic
 * rollback stays correct) and persists once on release. */
function TripDisplayPanel({
  trip,
  onPreview,
}: {
  trip: Trip;
  onPreview: (width: number | null) => void;
}) {
  const updateTrip = useUpdateTrip(trip.id);
  const [width, setWidth] = useState(trip.listWidth);

  useEffect(() => setWidth(trip.listWidth), [trip.listWidth]);

  // Live preview: drive the masonry behind the sheet from parent state as the
  // slider moves — no round-trip, and the cache (the persisted value) is left
  // untouched until release.
  function preview(next: number) {
    setWidth(next);
    onPreview(next);
  }

  function commit(next: number) {
    // Persist first so the optimistic cache update lands before we hand the layout
    // back to the cached value — avoids a one-frame flicker to the old width.
    if (next !== trip.listWidth) {
      updateTrip.mutate({ listWidth: next });
    }
    onPreview(null);
  }

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold">List width</h3>
        <span className="text-xs tabular-nums text-[var(--muted-foreground)]">{width}px</span>
      </div>
      <input
        type="range"
        min={LIST_WIDTH_MIN}
        max={LIST_WIDTH_MAX}
        step={20}
        value={width}
        onChange={(event) => preview(Number(event.target.value))}
        onPointerUp={(event) => commit(Number((event.target as HTMLInputElement).value))}
        onKeyUp={(event) => commit(Number((event.target as HTMLInputElement).value))}
        className="w-full accent-[var(--primary)]"
        aria-label="List column width"
      />
      <p className="text-xs text-[var(--muted-foreground)]">
        Narrower lists let more columns sit side by side.
      </p>
    </section>
  );
}

/**
 * Live collaboration for this trip (shown only when the account has the feature
 * enabled in Settings). Trip-level: flipping it changes the trip for everyone.
 */
function TripLivePanel({ trip }: { trip: Trip }) {
  const updateTrip = useUpdateTrip(trip.id);
  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <Label htmlFor={`trip-live-${trip.id}`} className="text-sm font-semibold">
            Live collaboration
          </Label>
          <p className="text-xs text-[var(--muted-foreground)]">
            Everyone on this trip sees each other’s edits as they happen, and who has an item open.
            Off holds other people’s changes until you tap Apply.
          </p>
        </div>
        <Switch
          id={`trip-live-${trip.id}`}
          checked={trip.liveCollaboration}
          onCheckedChange={(checked) => updateTrip.mutate({ liveCollaboration: checked })}
        />
      </div>
    </section>
  );
}

/**
 * The trip title's live mark: a pulsing teal dot while the stream is up, amber
 * while it reconnects. Quiet on purpose — presence marks on rows say who's here.
 */
function LiveDot({ status }: { status: StreamStatus | 'off' }) {
  const trouble = status === 'reconnecting' || status === 'stopped';
  const label = trouble
    ? 'Live collaboration — reconnecting'
    : status === 'live'
      ? 'Live collaboration on'
      : 'Live collaboration — connecting';
  return (
    <span className="relative inline-flex h-2.5 w-2.5 shrink-0" title={label}>
      <span className="sr-only">{label}</span>
      {status === 'live' ? (
        <span
          aria-hidden
          className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--primary)] opacity-60"
        />
      ) : null}
      <span
        aria-hidden
        className={cn(
          'relative inline-flex h-2.5 w-2.5 rounded-full',
          trouble ? 'bg-amber-500' : 'bg-[var(--primary)]',
          status === 'connecting' && 'opacity-50',
        )}
      />
    </span>
  );
}

/** Manage the lists in this trip — remove a whole list or enter page-level reorder mode. */
function TripListsPanel({
  trip,
  listReorderMode,
  onStartListReorder,
  onStopListReorder,
  onReconcile,
  onFindLinked,
  onPrint,
  onPromote,
  onNewList,
  onSelectAcross,
}: {
  trip: Trip;
  listReorderMode: boolean;
  onStartListReorder: () => void;
  onStopListReorder: () => void;
  onReconcile: (originMasterListId: string) => void;
  onFindLinked: () => void;
  onPrint: () => void;
  onPromote: (listName: string) => void;
  onNewList: () => void;
  onSelectAcross: () => void;
}) {
  const tripId = trip.id;
  const items = useTripItems(tripId);
  const removeList = useRemoveTripList(tripId);
  const [removeKey, setRemoveKey] = useState<string | null>(null);

  const groups = items.data ? groupTripItemsByOrigin(items.data) : [];
  const removing = groups.find((group) => group.key === removeKey) ?? null;

  if (groups.length === 0) {
    return null;
  }

  function confirmRemove() {
    if (!removing) {
      return;
    }
    // One operation — removes the whole list + records a single audit event.
    removeList.mutate({
      originMasterListId: removing.originId,
      originMasterListName: removing.kind === 'local' ? removing.name : null,
    });
    setRemoveKey(null);
  }

  return (
    <section className="flex flex-col gap-2">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-sm font-semibold">Lists in this trip</h3>
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-full sm:rounded-md"
            onClick={onNewList}
          >
            <Plus />
            New list
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-full sm:rounded-md"
            onClick={onPrint}
          >
            <Printer />
            <span className="sm:hidden">Print</span>
            <span className="hidden sm:inline">Print board</span>
          </Button>
          <CopyTripPromptButton trip={trip} />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-full sm:rounded-md"
            onClick={onFindLinked}
          >
            <Sparkles />
            <span className="sm:hidden">Find linked</span>
            <span className="hidden sm:inline">Find linked items</span>
          </Button>
          <Button
            type="button"
            variant={listReorderMode ? 'default' : 'outline'}
            size="sm"
            className="rounded-full sm:rounded-md"
            onClick={listReorderMode ? onStopListReorder : onStartListReorder}
          >
            <GripVertical />
            {listReorderMode ? 'Done' : 'Reorder'}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-full sm:rounded-md"
            onClick={onSelectAcross}
          >
            <CheckSquare2 />
            <span className="sm:hidden">Select</span>
            <span className="hidden sm:inline">Select across lists</span>
          </Button>
        </div>
      </div>
      <div className="flex flex-col divide-y divide-[var(--border)] overflow-hidden rounded-xl border border-[var(--border)]">
        {groups.map((group) => (
          <div key={group.key} className="flex items-center justify-between gap-3 p-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{group.name}</p>
              <p className="text-xs text-[var(--muted-foreground)]">
                {group.items.length} {group.items.length === 1 ? 'item' : 'items'}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {group.originId ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="px-2 sm:px-3"
                  aria-label="Audit list"
                  onClick={() => onReconcile(group.originId as string)}
                >
                  <Sparkles />
                  <span className="hidden sm:inline">Audit</span>
                </Button>
              ) : group.kind === 'local' ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="px-2 sm:px-3"
                  aria-label="Push list to Library"
                  onClick={() => onPromote(group.name)}
                >
                  <Sparkles />
                  <span className="hidden sm:inline">Push to Library</span>
                </Button>
              ) : null}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="px-2 text-[var(--destructive)] sm:px-3"
                aria-label="Remove list"
                onClick={() => setRemoveKey(group.key)}
              >
                <Trash2 />
                <span className="hidden sm:inline">Remove</span>
              </Button>
            </div>
          </div>
        ))}
      </div>

      <ConfirmDialog
        open={removeKey !== null}
        onOpenChange={(open) => !open && setRemoveKey(null)}
        title="Remove this list from the trip?"
        description={
          removing
            ? `All ${removing.items.length} item${removing.items.length === 1 ? '' : 's'} from “${removing.name}” will be removed from this trip.`
            : ''
        }
        confirmLabel="Remove"
        destructive
        pending={removeList.isPending}
        onConfirm={confirmRemove}
      />
    </section>
  );
}

/** One-tap, trip-wide changes that don't need a selection — each behind a confirm. */
function TripWideActionsPanel({ tripId }: { tripId: string }) {
  const items = useTripItems(tripId);
  const members = useTripMembers(tripId);
  const bulkUpdate = useBulkUpdateTripItems(tripId);
  const assign = useAssignTripItems(tripId);
  const [confirm, setConfirm] = useState<
    | { kind: 'unpack'; ids: string[] }
    | { kind: 'unflag'; ids: string[] }
    | { kind: 'assign'; ids: string[]; handledBy: string; name: string }
    | null
  >(null);
  const all = items.data ?? [];
  const packed = all.filter((item) => item.status === 'done').map((item) => item.id);
  const flagged = all.filter((item) => item.flagged).map((item) => item.id);
  const unassigned = all.filter((item) => item.handledBy === null).map((item) => item.id);
  const busy = bulkUpdate.isPending || assign.isPending;

  async function onConfirm() {
    if (!confirm) {
      return;
    }
    const current = confirm;
    setConfirm(null);
    try {
      if (current.kind === 'unpack') {
        await bulkUpdate.mutateAsync({ itemIds: current.ids, patch: { status: 'todo' } });
        toast.success(`Unchecked ${current.ids.length} items`);
      } else if (current.kind === 'unflag') {
        await bulkUpdate.mutateAsync({ itemIds: current.ids, patch: { flagged: false } });
        toast.success(`Cleared ${current.ids.length} flags`);
      } else {
        await assign.mutateAsync({ itemIds: current.ids, handledBy: current.handledBy });
        toast.success(`Assigned ${current.ids.length} items to ${current.name}`);
      }
    } catch {
      // Optimistic hooks roll back + toast.
    }
  }

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold">Whole trip</h3>
      <div className="flex flex-wrap items-center gap-1.5">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy || packed.length === 0}
          onClick={() => setConfirm({ kind: 'unpack', ids: packed })}
        >
          <RotateCcw />
          Uncheck everything
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy || flagged.length === 0}
          onClick={() => setConfirm({ kind: 'unflag', ids: flagged })}
        >
          <Flag />
          Clear all flags
        </Button>
        <select
          aria-label="Assign all unassigned items to"
          value=""
          disabled={busy || unassigned.length === 0}
          onChange={(event) => {
            const member = members.data?.find((m) => m.id === event.target.value);
            if (member) {
              setConfirm({
                kind: 'assign',
                ids: unassigned,
                handledBy: member.id,
                name: personName(member),
              });
            }
          }}
          className="h-10 rounded-md border border-[var(--input)] bg-[var(--background)] px-2 text-sm md:h-8"
        >
          <option value="">Assign all unassigned to…</option>
          {(members.data ?? []).map((member) => (
            <option key={member.id} value={member.id}>
              {personName(member)}
            </option>
          ))}
        </select>
      </div>
      <p className="text-[var(--muted-foreground)] text-xs">
        {packed.length} checked · {flagged.length} flagged · {unassigned.length} unassigned
      </p>
      <ConfirmDialog
        open={confirm !== null}
        onOpenChange={(open) => !open && setConfirm(null)}
        title={
          confirm?.kind === 'unpack'
            ? `Uncheck all ${confirm.ids.length} checked items?`
            : confirm?.kind === 'unflag'
              ? `Clear ${confirm.ids.length} flags?`
              : confirm
                ? `Assign ${confirm.ids.length} unassigned items to ${confirm.name}?`
                : ''
        }
        description={
          confirm?.kind === 'unpack'
            ? 'Resets the trip so you can pack it again. Final checks are cleared too.'
            : undefined
        }
        confirmLabel={
          confirm?.kind === 'unpack' ? 'Unpack' : confirm?.kind === 'unflag' ? 'Clear' : 'Assign'
        }
        pending={busy}
        onConfirm={() => void onConfirm()}
      />
    </section>
  );
}

/** The trip's archive: removed items with one-tap Restore. Hidden when empty. */
function TripRemovedItemsPanel({ tripId }: { tripId: string }) {
  const removed = useRemovedTripItems(tripId);
  const restore = useRestoreTripItem(tripId);
  const items = removed.data ?? [];

  if (items.length === 0) {
    return null;
  }

  async function onRestore(item: TripItemWithLabels) {
    try {
      await restore.mutateAsync(item.id);
      toast.success(`Restored “${item.name}”`, { position: 'bottom-center' });
    } catch {
      toast.error('Couldn’t restore that item');
    }
  }

  // Collapsed by default: removed lists land here too, so this can run long.
  return (
    <details className="group rounded-xl border border-[var(--border)]">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-3">
        <span className="flex items-center gap-2 text-sm font-semibold">
          <Archive className="h-4 w-4" />
          Removed items
          <Badge variant="secondary">{items.length}</Badge>
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-[var(--muted-foreground)] transition-transform group-open:rotate-180" />
      </summary>
      <div className="flex max-h-80 flex-col divide-y divide-[var(--border)] overflow-y-auto border-t border-[var(--border)]">
        {items.map((item) => (
          <div key={item.id} className="flex items-center justify-between gap-3 p-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{item.name}</p>
              <p className="truncate text-xs text-[var(--muted-foreground)]">
                {item.originMasterListName ?? 'Manually added items'}
                {item.deletedAt ? ` · removed ${tripItemEventTimeAgo(item.deletedAt)}` : ''}
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="shrink-0"
              disabled={restore.isPending}
              onClick={() => void onRestore(item)}
            >
              <RotateCcw />
              Restore
            </Button>
          </div>
        ))}
      </div>
    </details>
  );
}

function eventGroupKey(event: TripItemEvent): string {
  return tripListKey(event);
}

function eventGroupName(event: TripItemEvent): string {
  return event.originMasterListName ?? 'Manually added items';
}

function TripActivityPanel({ tripId }: { tripId: string }) {
  const events = useTripEvents(tripId);
  const [group, setGroup] = useState('all');
  const list = events.data ?? [];
  const groups = new Map<string, string>();
  for (const event of list) {
    groups.set(eventGroupKey(event), eventGroupName(event));
  }
  const filtered = group === 'all' ? list : list.filter((event) => eventGroupKey(event) === group);

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold">Activity</h3>
        <select
          value={group}
          onChange={(event) => setGroup(event.target.value)}
          className="h-8 max-w-[12rem] rounded-md border border-[var(--input)] bg-[var(--background)] px-2 text-sm"
          aria-label="Filter activity"
        >
          <option value="all">All activity</option>
          {[...groups.entries()].map(([key, name]) => (
            <option key={key} value={key}>
              {name}
            </option>
          ))}
        </select>
      </div>
      {events.isLoading ? (
        <div className="grid gap-2 rounded-xl border border-[var(--border)] p-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-3/4" />
        </div>
      ) : events.isError ? (
        <p className="rounded-xl border border-[var(--border)] p-3 text-sm text-[var(--muted-foreground)]">
          Couldn’t load activity.
        </p>
      ) : filtered.length ? (
        <ol className="flex max-h-80 flex-col gap-0 overflow-auto rounded-xl border border-[var(--border)] p-3">
          {filtered.map((event, index) => (
            <li key={event.id} className="flex gap-3">
              <div className="flex w-4 flex-col items-center">
                <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[var(--primary)]" />
                {index < filtered.length - 1 ? (
                  <span className="w-px flex-1 bg-[var(--border)]" />
                ) : null}
              </div>
              <div className="min-w-0 flex-1 pb-4">
                <p className="text-sm">
                  <span className="font-medium">{event.actorName ?? 'Someone'}</span>{' '}
                  <span className="text-[var(--muted-foreground)]">
                    {describeTripItemEvent(event)}
                  </span>
                </p>
                <p className="truncate text-xs text-[var(--muted-foreground)]">
                  {event.tripItemName ? (
                    <>
                      {event.tripItemName}
                      {event.itemDeletedAt ? ' · deleted' : ''} ·{' '}
                    </>
                  ) : null}
                  {eventGroupName(event)} · {tripItemEventTimeAgo(event.createdAt)}
                </p>
                <TripItemEventDetails event={event} />
              </div>
            </li>
          ))}
        </ol>
      ) : (
        <p className="rounded-xl border border-[var(--border)] p-3 text-sm text-[var(--muted-foreground)]">
          No activity yet.
        </p>
      )}
    </section>
  );
}

/** Archive / restore, tucked into a danger zone at the bottom of Manage. */
function TripDangerZone({ trip }: { trip: Trip }) {
  const closeTrip = useCloseTrip(trip.id);
  const reopenTrip = useReopenTrip(trip.id);
  const archived = trip.status === 'closed';
  const lifecycle = archived ? reopenTrip : closeTrip;

  async function onLifecycle() {
    try {
      await lifecycle.mutateAsync();
      toast.success(archived ? 'Trip restored' : 'Trip archived');
    } catch {
      toast.error('Couldn’t update the trip');
    }
  }

  return (
    <section className="flex flex-col gap-3 rounded-xl border border-[var(--border)] p-4">
      <div className="space-y-0.5">
        <h3 className="text-sm font-semibold">Danger zone</h3>
        <p className="text-sm text-[var(--muted-foreground)]">
          {archived
            ? 'This trip is archived. Restore it to make it active again.'
            : 'Archive this trip to hide it from your active list. You can restore it anytime.'}
        </p>
      </div>
      <Button
        type="button"
        variant="outline"
        onClick={onLifecycle}
        disabled={lifecycle.isPending}
        className="self-start text-[var(--destructive)]"
      >
        {archived ? <RotateCcw /> : <Archive />}
        {archived ? 'Restore trip' : 'Archive trip'}
      </Button>
    </section>
  );
}

function TripCollaboratorsPanel({ tripId }: { tripId: string }) {
  const collaborators = useTripCollaborators(tripId);
  const invites = useTripInvites(tripId);
  const createInvite = useCreateTripInvite(tripId);
  const cancelInvite = useCancelTripInvite(tripId);
  const updateCollaborator = useUpdateTripCollaborator(tripId);
  const deleteCollaborator = useDeleteTripCollaborator(tripId);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<TripCollaboratorWithUser['role']>('editor');

  async function onInvite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const parsedRole = ShareRole.parse(role);
    try {
      await createInvite.mutateAsync({ email, role: parsedRole });
      setEmail('');
      setRole('editor');
      toast.success('Invitation sent');
    } catch {
      toast.error('Couldn’t send invitation');
    }
  }

  async function onCancelInvite(inviteId: string) {
    try {
      await cancelInvite.mutateAsync(inviteId);
      toast.success('Invitation canceled');
    } catch {
      toast.error('Couldn’t cancel invitation');
    }
  }

  async function onRoleChange(collaborator: TripCollaboratorWithUser, nextRole: string) {
    const parsedRole = ShareRole.safeParse(nextRole);
    if (!parsedRole.success || parsedRole.data === collaborator.role) {
      return;
    }
    try {
      await updateCollaborator.mutateAsync({
        collaboratorId: collaborator.id,
        body: { role: parsedRole.data },
      });
      toast.success('Collaborator updated');
    } catch {
      toast.error('Couldn’t update collaborator');
    }
  }

  async function onRemove(collaboratorId: string) {
    try {
      await deleteCollaborator.mutateAsync(collaboratorId);
      toast.success('Collaborator removed');
    } catch {
      toast.error('Couldn’t remove collaborator');
    }
  }

  return (
    <Card>
      {/* Collapsible: collaborators is a secondary admin action — collapsed by
          default so it never crowds the checklist (matters most on mobile). */}
      <details className="group">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-2 p-6">
          <span className="flex flex-col gap-1.5">
            <span className="flex items-center gap-2 text-base font-semibold">
              <Users className="h-4 w-4" />
              Collaborators
            </span>
            <span className="text-sm text-[var(--muted-foreground)]">
              Invite helpers by email — they accept it from their own Trips screen.
            </span>
          </span>
          <ChevronDown className="h-5 w-5 shrink-0 text-[var(--muted-foreground)] transition-transform group-open:rotate-180" />
        </summary>
        <CardContent className="flex flex-col gap-4 pt-0">
          <form onSubmit={onInvite} className="grid gap-3 sm:grid-cols-[1fr_8rem_auto]">
            <div className="grid gap-1">
              <Label htmlFor="trip-collaborator-email">Email</Label>
              <Input
                id="trip-collaborator-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="friend@example.com"
              />
            </div>
            <div className="grid gap-1">
              <Label htmlFor="trip-collaborator-role">Role</Label>
              <select
                id="trip-collaborator-role"
                value={role}
                onChange={(event) => setRole(ShareRole.parse(event.target.value))}
                className="h-10 rounded-md border border-[var(--input)] bg-[var(--background)] px-3 text-sm"
              >
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
              </select>
            </div>
            <Button
              type="submit"
              className="self-end"
              disabled={createInvite.isPending || email.trim().length === 0}
            >
              Invite
            </Button>
          </form>

          {invites.data?.length ? (
            <div className="flex flex-col gap-2">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Pending invitations
              </p>
              <div className="divide-y divide-[var(--border)] rounded-md border border-[var(--border)]">
                {invites.data.map((invite) => (
                  <div
                    key={invite.id}
                    className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <p className="truncate text-sm font-medium">{invite.email}</p>
                      <Badge variant="secondary">Pending</Badge>
                      <Badge variant="outline">{invite.role}</Badge>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="self-start text-[var(--destructive)] sm:self-auto"
                      disabled={cancelInvite.isPending}
                      onClick={() => void onCancelInvite(invite.id)}
                    >
                      Cancel
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {collaborators.isLoading ? (
            <Skeleton className="h-14" />
          ) : collaborators.isError ? (
            <p className="text-sm text-[var(--muted-foreground)]">Couldn’t load collaborators.</p>
          ) : !collaborators.data?.length ? (
            <p className="text-sm text-[var(--muted-foreground)]">No collaborators yet.</p>
          ) : (
            <div className="divide-y divide-[var(--border)] rounded-md border border-[var(--border)]">
              {collaborators.data.map((collaborator) => (
                <div
                  key={collaborator.id}
                  className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">
                      {collaborator.displayName || collaborator.email}
                    </p>
                    <p className="truncate text-xs text-[var(--muted-foreground)]">
                      {collaborator.email}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={collaborator.role}
                      onChange={(event) => void onRoleChange(collaborator, event.target.value)}
                      className="h-9 rounded-md border border-[var(--input)] bg-[var(--background)] px-2 text-sm"
                      disabled={updateCollaborator.isPending}
                    >
                      <option value="viewer">Viewer</option>
                      <option value="editor">Editor</option>
                    </select>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-[var(--destructive)]"
                      disabled={deleteCollaborator.isPending}
                      onClick={() => void onRemove(collaborator.id)}
                    >
                      <Trash2 />
                      <span className="sr-only">Remove collaborator</span>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </details>
    </Card>
  );
}

function GuidedPullIn({ tripId }: { tripId: string }) {
  const activities = useActivities();
  const masterLists = useMasterLists();
  const sharedMasterLists = useSharedMasterLists();
  const tripItems = useTripItems(tripId);
  const pullIntoTrip = usePullIntoTrip(tripId);
  const removeTripList = useRemoveTripList(tripId);
  const [activityIds, setActivityIds] = useState<string[]>([]);
  const [masterListIds, setMasterListIds] = useState<string[]>([]);

  function toggle(list: string[], id: string, checked: boolean) {
    return checked ? Array.from(new Set([...list, id])) : list.filter((item) => item !== id);
  }

  // Bottom snackbar (thumb-reachable, swipe-down to dismiss) instead of a top
  // toast. Undo removes the just-pulled lists whole — a fresh pull is the only
  // source of items with those origin lists, so lists/remove is an exact revert.
  function showPulledSnackbar(pulled: TripItemWithLabels[]) {
    if (pulled.length === 0) {
      toast('Nothing new to pull in', { position: 'bottom-center' });
      return;
    }
    const originListIds = Array.from(
      new Set(
        pulled.map((item) => item.originMasterListId).filter((id): id is string => id !== null),
      ),
    );
    toast.success(`Added ${pulled.length} item${pulled.length === 1 ? '' : 's'}`, {
      position: 'bottom-center',
      duration: 6000,
      action: {
        label: 'Undo',
        onClick: () => {
          for (const listId of originListIds) {
            removeTripList.mutate({ originMasterListId: listId, originMasterListName: null });
          }
        },
      },
    });
  }

  async function onPullIn() {
    try {
      const pulled = await pullIntoTrip.mutateAsync({ activityIds, masterListIds });
      setActivityIds([]);
      setMasterListIds([]);
      showPulledSnackbar(pulled);
    } catch {
      toast.error('Couldn’t pull items into trip');
    }
  }

  async function pullSingleMasterList(masterListId: string) {
    try {
      const pulled = await pullIntoTrip.mutateAsync({
        activityIds: [],
        masterListIds: [masterListId],
      });
      showPulledSnackbar(pulled);
    } catch {
      toast.error('Couldn’t pull items into trip');
    }
  }

  async function pullSingleActivity(activityId: string) {
    try {
      const pulled = await pullIntoTrip.mutateAsync({
        activityIds: [activityId],
        masterListIds: [],
      });
      showPulledSnackbar(pulled);
    } catch {
      toast.error('Couldn’t pull items into trip');
    }
  }

  const selectionCount = activityIds.length + masterListIds.length;
  const ownedLists = masterLists.data ?? [];
  const sharedLists = sharedMasterLists.data ?? [];
  // Lists already pulled into this trip can't be pulled again — hide them.
  const pulledListIds = new Set(
    (tripItems.data ?? [])
      .map((item) => item.originMasterListId)
      .filter((id): id is string => id !== null),
  );
  const listSources = [
    ...ownedLists.map((list) => ({ list, source: 'Mine' })),
    ...sharedLists.map((list) => ({ list, source: 'Shared' })),
  ].filter(({ list }) => !pulledListIds.has(list.id));
  const isLoadingLists = masterLists.isLoading || sharedMasterLists.isLoading;
  const isListError = masterLists.isError || sharedMasterLists.isError;

  return (
    <aside className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Planner</CardTitle>
          <CardDescription>
            Pull or drag Activities and Master Lists into this trip.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-5">
          <section className="flex flex-col gap-3">
            <h2 className="text-sm font-medium">Activities</h2>
            {activities.isLoading ? (
              <Skeleton className="h-20" />
            ) : activities.isError || !activities.data?.length ? (
              <p className="text-sm text-[var(--muted-foreground)]">No Activities yet.</p>
            ) : (
              <div className="flex flex-col gap-3">
                {activities.data.map((activity) => (
                  <ActivityChoice
                    key={activity.id}
                    activity={activity}
                    checked={activityIds.includes(activity.id)}
                    isPulling={pullIntoTrip.isPending}
                    onCheckedChange={(checked) =>
                      setActivityIds((current) => toggle(current, activity.id, checked))
                    }
                    onPullIn={() => void pullSingleActivity(activity.id)}
                  />
                ))}
              </div>
            )}
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-sm font-medium">Master Lists</h2>
            {isLoadingLists ? (
              <Skeleton className="h-20" />
            ) : isListError || listSources.length === 0 ? (
              <p className="text-sm text-[var(--muted-foreground)]">No Master Lists yet.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {listSources.map(({ list, source }) => {
                  return (
                    <MasterListChoice
                      key={list.id}
                      list={list}
                      source={source}
                      checked={masterListIds.includes(list.id)}
                      isPulling={pullIntoTrip.isPending}
                      onCheckedChange={(checked) =>
                        setMasterListIds((current) => toggle(current, list.id, checked))
                      }
                      onPullIn={() => void pullSingleMasterList(list.id)}
                    />
                  );
                })}
              </div>
            )}
          </section>

          <Button
            type="button"
            onClick={onPullIn}
            disabled={pullIntoTrip.isPending || selectionCount === 0}
          >
            <PackagePlus />
            Pull in selection
          </Button>
        </CardContent>
      </Card>
    </aside>
  );
}

function MasterListChoice({
  list,
  source,
  checked,
  isPulling,
  onCheckedChange,
  onPullIn,
}: {
  list: MasterList;
  source: string;
  checked: boolean;
  isPulling: boolean;
  onCheckedChange: (checked: boolean) => void;
  onPullIn: () => void;
}) {
  const { attributes, isDragging, listeners, setNodeRef, transform } = useDraggable({
    id: `planner-master-list-${list.id}`,
    data: { type: 'master-list', masterListId: list.id } satisfies TripDragData,
  });
  const checkboxId = `master-list-${list.id}`;

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform) }}
      className={cn(
        'flex flex-col gap-2.5 rounded-md border border-[var(--border)] p-3 text-sm',
        isDragging && 'opacity-60',
      )}
    >
      <div className="flex items-start gap-3">
        <Checkbox
          id={checkboxId}
          className="mt-0.5"
          checked={checked}
          onCheckedChange={(value) => onCheckedChange(value === true)}
        />
        <Label htmlFor={checkboxId} className="min-w-0 flex-1 leading-normal">
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{list.name}</span>
            <Badge variant={source === 'Shared' ? 'secondary' : 'outline'}>{source}</Badge>
          </span>
          {list.description ? (
            <span className="mt-0.5 line-clamp-2 block text-[var(--muted-foreground)]">
              {list.description}
            </span>
          ) : null}
        </Label>
      </div>
      <div className="flex items-center justify-end gap-1">
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-8 w-8 cursor-grab active:cursor-grabbing"
          aria-label={`Drag ${list.name}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical />
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onPullIn} disabled={isPulling}>
          <PackagePlus />
          Pull
        </Button>
      </div>
    </div>
  );
}

function ActivityChoice({
  activity,
  checked,
  isPulling,
  onCheckedChange,
  onPullIn,
}: {
  activity: Activity;
  checked: boolean;
  isPulling: boolean;
  onCheckedChange: (checked: boolean) => void;
  onPullIn: () => void;
}) {
  const detail = useActivity(activity.id);
  const { attributes, isDragging, listeners, setNodeRef, transform } = useDraggable({
    id: `planner-activity-${activity.id}`,
    data: { type: 'activity', activityId: activity.id } satisfies TripDragData,
  });

  const checkboxId = `activity-${activity.id}`;

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform) }}
      className={cn(
        'flex flex-col gap-2.5 rounded-md border border-[var(--border)] p-3 text-sm',
        isDragging && 'opacity-60',
      )}
    >
      <div className="flex items-start gap-3">
        <Checkbox
          id={checkboxId}
          className="mt-0.5"
          checked={checked}
          onCheckedChange={(value) => onCheckedChange(value === true)}
        />
        <Label htmlFor={checkboxId} className="min-w-0 flex-1 leading-normal">
          <span className="block font-medium">{activity.name}</span>
          {activity.prompt ? (
            <span className="mt-0.5 line-clamp-2 block text-[var(--muted-foreground)]">
              {activity.prompt}
            </span>
          ) : null}
          <span className="mt-2 flex flex-wrap gap-1">
            {detail.isLoading ? (
              <span className="text-xs text-[var(--muted-foreground)]">
                Loading bundled lists...
              </span>
            ) : detail.data?.masterLists.length ? (
              detail.data.masterLists.map((list) => <Badge key={list.id}>{list.name}</Badge>)
            ) : (
              <span className="text-xs text-[var(--muted-foreground)]">No bundled lists</span>
            )}
          </span>
        </Label>
      </div>
      <div className="flex items-center justify-end gap-1">
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-8 w-8 cursor-grab active:cursor-grabbing"
          aria-label={`Drag ${activity.name}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical />
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onPullIn} disabled={isPulling}>
          <PackagePlus />
          Pull
        </Button>
      </div>
    </div>
  );
}

/** The item slide-out: Details by default, with Activity and Lessons one tap away. */
function TripItemSheetBody({
  tripId,
  item,
  labels,
  tab,
  onTabChange,
  onDeleted,
}: {
  tripId: string;
  item: TripItemWithLabels;
  labels: string[];
  tab: TripItemTab;
  onTabChange: (tab: TripItemTab) => void;
  onDeleted: () => void;
}) {
  return (
    <div className="flex min-h-full flex-col gap-0">
      <Tabs
        value={tab}
        onValueChange={(next) => onTabChange(next as TripItemTab)}
        className="m-4 mb-3 mr-16 self-start"
        tabs={[
          { value: 'details', label: 'Details' },
          { value: 'activity', label: 'Activity' },
          { value: 'lessons', label: 'Lessons' },
        ]}
      />
      {tab === 'details' ? (
        <TripItemDetailsPanel tripId={tripId} item={item} labels={labels} onDeleted={onDeleted} />
      ) : tab === 'activity' ? (
        <TripItemActivity tripId={tripId} itemId={item.id} />
      ) : (
        <TripItemLessonsPanel tripId={tripId} item={item} />
      )}
    </div>
  );
}

/** The `details.current` of a 409 from the item PATCH, or null for any other error. */
function conflictDetails(error: unknown) {
  if (!(error instanceof ApiError) || error.code !== 'CONFLICT') {
    return null;
  }
  const parsed = TripItemConflict.safeParse(error.details);
  return parsed.success ? parsed.data.current : null;
}

/** Who most recently made this kind of change to the item, from the live batch. */
function lastEditorOf(itemId: string, type: TripItemEvent['type']): string | null {
  const { applied } = useTripChangesStore.getState();
  return (
    applied.find((event) => event.tripItemId === itemId && event.type === type)?.actorName ?? null
  );
}

/**
 * Someone else saved a different value for a field you were editing. Your
 * draft stays put until you pick a side.
 */
function ConflictBanner({
  draft,
  actorName,
  onKeepMine,
}: {
  draft: FieldDraft;
  actorName: string | null;
  onKeepMine: () => void;
}) {
  const theirs = draft.conflict?.theirs ?? '';
  return (
    <output className="grid gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm dark:border-amber-700 dark:bg-amber-950/40">
      <p className="text-amber-900 dark:text-amber-200">
        {actorName ?? 'Someone else'} changed this to{' '}
        <span className="font-medium">{theirs.length > 0 ? `“${theirs}”` : 'empty'}</span> while you
        were editing.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="outline" onClick={draft.useTheirs}>
          Use theirs
        </Button>
        <Button type="button" size="sm" onClick={onKeepMine}>
          Keep mine
        </Button>
      </div>
    </output>
  );
}

/** Other live tabs with this item open — a heads-up before you both rewrite it. */
function PresenceBanner({
  viewers,
  members,
}: {
  viewers: PresenceEntry[];
  members: TripMember[] | undefined;
}) {
  const color = colorByMember(members);
  const editing = viewers.filter((viewer) => viewer.editing);
  const names = (list: PresenceEntry[]) =>
    list.map((viewer) => viewer.displayName ?? viewer.email).join(', ');
  return (
    <div className="flex items-center gap-2 rounded-md border border-[var(--primary)]/40 bg-[var(--accent)]/60 px-2.5 py-2 text-sm">
      <span className="-space-x-1 inline-flex shrink-0">
        {viewers.slice(0, 3).map((viewer) => (
          <PersonInitial
            key={viewer.connectionId}
            user={{ id: viewer.userId, email: viewer.email, displayName: viewer.displayName }}
            color={color(viewer.userId)}
            decorative
            className="ring-2 ring-[var(--card)]"
          />
        ))}
      </span>
      <span className="min-w-0 truncate">
        {editing.length > 0
          ? `${names(editing)} ${editing.length === 1 ? 'is' : 'are'} editing this item`
          : `${names(viewers)} also ${viewers.length === 1 ? 'has' : 'have'} this open`}
      </span>
    </div>
  );
}

/** Live mode: the open item was removed by someone else while this sheet showed it. */
function RemovedItemState({
  actorName,
  onRestore,
  onClose,
}: {
  actorName: string | null;
  onRestore: () => void;
  onClose: () => void;
}) {
  return (
    <div className="flex min-h-full flex-col items-start gap-3 p-4 pr-16">
      <h2 className="text-lg font-semibold">Removed from the trip</h2>
      <p className="text-sm text-[var(--muted-foreground)]">
        {actorName ?? 'Someone else'} removed this item just now. It’s in the trip’s archive, so you
        can bring it straight back.
      </p>
      <div className="flex gap-2">
        <Button type="button" onClick={onRestore}>
          Restore item
        </Button>
        <Button type="button" variant="outline" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
}

function TripItemDetailsPanel({
  tripId,
  item,
  labels,
  onDeleted,
}: {
  tripId: string;
  item: TripItemWithLabels;
  labels: string[];
  onDeleted: () => void;
}) {
  // Text fields keep a local draft that a remote edit can't overwrite mid-typing;
  // commits carry the value they were based on (compare-and-set, 409 on a miss).
  const nameDraft = useFieldDraft(item.name);
  const descriptionDraft = useFieldDraft(item.description ?? '');
  const [labelDraft, setLabelDraft] = useState('');
  // Per-panel edit toggle: the detail panel reads as a clean card by default;
  // Edit unlocks the fields. Status/flag/comments stay live in either state.
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const updateItem = useUpdateTripItem(tripId);
  const deleteItem = useDeleteTripItem(tripId);
  const updateStatus = useUpdateTripItemStatus(tripId);
  const updateFlag = useUpdateTripItemFlag(tripId);
  const addLabel = useAddTripItemLabel(tripId);
  const removeLabel = useDeleteTripItemLabel(tripId);
  const linkItems = useLinkTripItems(tripId);
  const unlinkItem = useUnlinkTripItem(tripId);
  const allItems = useTripItems(tripId);
  const members = useTripMembers(tripId);
  const me = useMe();
  const setMine = useTripPresence((state) => state.setMine);
  const viewers = useTripPresence((state) => state.byItem[item.id]);
  const handledByLabel = item.handledBy
    ? `Handled by ${userDisplayName(item.handledByUser)}`
    : 'Unassigned';

  // Cross-list link state, resolved over all live trip items.
  const otherItems = (allItems.data ?? []).filter((other) => other.id !== item.id);
  const leafCount = otherItems.filter((other) => other.controlledByItemId === item.id).length;
  const isLeaf = Boolean(item.controlledByItemId);
  // Items you can link this one TO (this item becomes/extends the controller):
  // a different list, and not itself already linked into a group.
  const controllerIds = new Set(
    otherItems.map((other) => other.controlledByItemId).filter((id): id is string => id !== null),
  );
  const linkCandidates = otherItems.filter(
    (other) =>
      !other.controlledByItemId &&
      !controllerIds.has(other.id) &&
      // Compare full list keys, not just origin ids — two different trip-local
      // lists both have a null origin but are NOT the same list.
      tripListKey(other) !== tripListKey(item),
  );
  // The full link group (this item + everyone sharing its controller), so the
  // panel can show every list this item is linked across.
  const linkRootId = item.controlledByItemId ?? item.id;
  const linkGroup = (allItems.data ?? []).filter(
    (member) => member.id === linkRootId || member.controlledByItemId === linkRootId,
  );

  // Opening a different item starts in read mode with no pending confirm.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset only on item switch
  useEffect(() => {
    setEditing(false);
    setConfirmDelete(false);
  }, [item.id]);
  // Presence: other live tabs glow this item harder while Edit is on here.
  useEffect(() => {
    setMine({ editing });
    return () => setMine({ editing: false });
  }, [editing, setMine]);

  // These mutations are optimistic: the cache change is the feedback, and the
  // hooks own rollback + the error toast — so the handlers just fire and forget.
  // Name/description auto-save on blur (matching the library item editor); only
  // commit when the trimmed value actually changed, and never persist an empty name.
  function saveName(next: string, expect: string) {
    updateItem.mutate(
      { itemId: item.id, body: { name: next, expect: { name: expect } } },
      {
        onError: (error) => {
          const conflict = conflictDetails(error);
          if (conflict) {
            nameDraft.conflictWith(conflict.name, next);
          }
        },
      },
    );
  }

  function saveDescription(next: string, expect: string) {
    updateItem.mutate(
      {
        itemId: item.id,
        body: { description: next || null, expect: { description: expect || null } },
      },
      {
        onError: (error) => {
          const conflict = conflictDetails(error);
          if (conflict) {
            descriptionDraft.conflictWith(conflict.description ?? '', next);
          }
        },
      },
    );
  }

  function commitName() {
    if (nameDraft.conflict) {
      return; // resolve the banner first
    }
    const next = nameDraft.value.trim();
    if (next === nameDraft.base) {
      return;
    }
    if (next.length === 0) {
      nameDraft.setValue(nameDraft.base);
      return;
    }
    saveName(next, nameDraft.base);
  }

  function commitDescription() {
    if (descriptionDraft.conflict) {
      return;
    }
    const next = descriptionDraft.value.trim();
    if (next === descriptionDraft.base) {
      return;
    }
    saveDescription(next, descriptionDraft.base);
  }

  function onDelete() {
    // Close the sheet at once — the optimistic delete empties the open item, so
    // waiting would flash a blank panel.
    onDeleted();
    deleteItem.mutate(item.id);
  }

  function setStatus(status: 'todo' | 'done') {
    updateStatus.mutate({ itemId: item.id, body: { status } });
  }

  function toggleFlag() {
    updateFlag.mutate({ itemId: item.id, body: { flagged: !item.flagged } });
  }

  function setHandledBy(handledBy: string | null) {
    updateItem.mutate({ itemId: item.id, body: { handledBy } });
  }

  function onAddLabel(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const label = labelDraft.trim();
    if (!label) {
      return;
    }
    setLabelDraft('');
    addLabel.mutate({ itemId: item.id, body: { label } });
  }

  function onRemoveLabel(label: string) {
    removeLabel.mutate({ itemId: item.id, body: { label } });
  }

  return (
    <Card className="rounded-none border-x-0 shadow-none sm:rounded-none">
      <CardContent className="p-4">
        <div className="grid gap-3">
          {viewers && viewers.length > 0 ? (
            <PresenceBanner viewers={viewers} members={members.data} />
          ) : null}
          <div className="grid grid-cols-[1fr_1fr_auto] gap-1 rounded-md border border-[var(--border)] p-1">
            <Button
              type="button"
              size="sm"
              variant={item.status !== 'done' ? 'default' : 'ghost'}
              onClick={() => void setStatus('todo')}
            >
              <Square />
              todo
            </Button>
            <Button
              type="button"
              size="sm"
              variant={item.status === 'done' ? 'default' : 'ghost'}
              onClick={() => void setStatus('done')}
            >
              <CheckCircle2 />
              done
            </Button>
            <Button
              type="button"
              size="icon"
              variant={item.flagged ? 'default' : 'ghost'}
              className={cn(item.flagged && 'bg-red-600 text-white hover:bg-red-700')}
              aria-label={item.flagged ? 'Unflag item' : 'Flag item'}
              onClick={() => void toggleFlag()}
            >
              <Flag className={cn(item.flagged && 'fill-white')} />
            </Button>
          </div>
          {/* Edit toggle — unlocks the editable fields below. */}
          <div className="flex justify-end">
            <Button
              type="button"
              size="sm"
              variant={editing ? 'default' : 'outline'}
              onClick={() => setEditing((value) => !value)}
            >
              {editing ? <Check /> : <Pencil />}
              {editing ? 'Done' : 'Edit'}
            </Button>
          </div>

          {editing ? (
            <div className="grid gap-2">
              <Label htmlFor={`trip-item-name-${item.id}`}>Name</Label>
              <Input
                id={`trip-item-name-${item.id}`}
                aria-label="Item name"
                className="text-black dark:text-white"
                value={nameDraft.value}
                onChange={(event) => nameDraft.setValue(event.target.value)}
                onBlur={commitName}
                required
              />
              {nameDraft.conflict ? (
                <ConflictBanner
                  draft={nameDraft}
                  actorName={lastEditorOf(item.id, 'renamed')}
                  onKeepMine={() => {
                    const { value, expect } = nameDraft.keepMine();
                    saveName(value.trim(), expect);
                  }}
                />
              ) : null}
            </div>
          ) : (
            <h2 className="text-lg font-semibold text-black dark:text-white">{item.name}</h2>
          )}
          {editing ? (
            <div className="grid gap-2">
              <Label htmlFor={`trip-item-description-${item.id}`}>Description</Label>
              <Textarea
                id={`trip-item-description-${item.id}`}
                className="text-black dark:text-white"
                value={descriptionDraft.value}
                onChange={(event) => descriptionDraft.setValue(event.target.value)}
                onBlur={commitDescription}
                rows={2}
              />
              {descriptionDraft.conflict ? (
                <ConflictBanner
                  draft={descriptionDraft}
                  actorName={lastEditorOf(item.id, 'note_changed')}
                  onKeepMine={() => {
                    const { value, expect } = descriptionDraft.keepMine();
                    saveDescription(value.trim(), expect);
                  }}
                />
              ) : null}
            </div>
          ) : item.description ? (
            <p className="whitespace-pre-wrap text-black dark:text-white">{item.description}</p>
          ) : null}
          <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--muted-foreground)]">
            <span>#{item.position + 1}</span>
            {item.originMasterListName ? (
              <Badge>{item.originMasterListName}</Badge>
            ) : (
              <Badge>Manual</Badge>
            )}
            <Badge>{handledByLabel}</Badge>
            {item.labels.map((label) => (
              <Badge key={label}>
                {label}
                {editing ? (
                  <button
                    type="button"
                    aria-label={`Remove ${label}`}
                    className="rounded-full p-0.5 hover:bg-[var(--background)]/40"
                    onClick={() => void onRemoveLabel(label)}
                  >
                    <X className="h-3 w-3" />
                  </button>
                ) : null}
              </Badge>
            ))}
          </div>
          {/* Link status — always visible, read-only. Lists every list this item
              is linked across (linked items share check-off + status). */}
          {linkGroup.length > 1 ? (
            <div className="flex flex-col gap-2 rounded-lg border border-[var(--border)] p-3">
              <span className="flex items-center gap-1.5 text-sm font-medium text-[var(--primary)]">
                <Link2 className="h-4 w-4" />
                Linked across {linkGroup.length} lists
              </span>
              <ul className="flex flex-col gap-1.5 text-sm">
                {linkGroup.map((member) => (
                  <li key={member.id} className="flex items-center gap-2">
                    <span className="text-black dark:text-white">
                      {member.originMasterListName ?? 'Manually added'}
                    </span>
                    {member.id === item.id ? (
                      <Badge variant="secondary" className="text-xs">
                        This one
                      </Badge>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* Edit-only field controls: assignee, linking, labels. */}
          {editing ? (
            <div className="grid gap-3">
              <div className="grid gap-2">
                <Label htmlFor={`trip-item-handled-by-${item.id}`}>Handled by</Label>
                <select
                  id={`trip-item-handled-by-${item.id}`}
                  value={item.handledBy ?? ''}
                  disabled={updateItem.isPending}
                  onChange={(event) => void setHandledBy(event.target.value || null)}
                  className="h-11 rounded-md border border-[var(--input)] bg-[var(--background)] px-3 text-base md:h-9 md:text-sm"
                >
                  <option value="">Unassigned</option>
                  {(members.data ?? []).map((member) => (
                    <option key={member.id} value={member.id}>
                      {personName(member)}
                      {member.id === me.data?.id ? ' (me)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {isLeaf || leafCount > 0 ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="w-fit"
                  disabled={unlinkItem.isPending}
                  onClick={() => unlinkItem.mutate(item.id)}
                >
                  <Unlink />
                  {isLeaf ? 'Unlink' : 'Unlink all'}
                </Button>
              ) : linkCandidates.length > 0 ? (
                <select
                  aria-label="Link to another item"
                  value=""
                  disabled={linkItems.isPending}
                  onChange={(event) => {
                    const targetId = event.target.value;
                    if (targetId) {
                      linkItems.mutate({ controllerId: item.id, linkedItemIds: [targetId] });
                    }
                  }}
                  className="h-9 w-full rounded-md border border-[var(--input)] bg-[var(--background)] px-3 text-sm"
                >
                  <option value="">🔗 Link to another item…</option>
                  {linkCandidates.map((candidate) => (
                    <option key={candidate.id} value={candidate.id}>
                      {candidate.name} ({candidate.originMasterListName ?? 'Manual'})
                    </option>
                  ))}
                </select>
              ) : null}

              <form onSubmit={onAddLabel} className="flex gap-2">
                <Input
                  value={labelDraft}
                  onChange={(event) => setLabelDraft(event.target.value)}
                  placeholder="Add label"
                  list="trip-labels"
                />
                <datalist id="trip-labels">
                  {labels.map((label) => (
                    <option key={label} value={label} />
                  ))}
                </datalist>
                <Button type="submit" size="sm" variant="outline" disabled={!labelDraft.trim()}>
                  <Plus />
                  Add
                </Button>
              </form>
            </div>
          ) : null}
          <TripItemCommentsPanel tripId={tripId} itemId={item.id} currentUserId={me.data?.id} />
          {/* Always-visible footer action: removing a single item shouldn't require
              entering Edit mode. Guarded by a confirm since it's destructive (no undo). */}
          <div className="flex flex-wrap gap-2 border-t border-[var(--border)] pt-3">
            <Button
              type="button"
              size="sm"
              variant="destructive"
              onClick={() => setConfirmDelete(true)}
              disabled={deleteItem.isPending}
            >
              <Trash2 />
              Remove from trip
            </Button>
          </div>
        </div>
      </CardContent>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Remove this item from the trip?"
        description={`“${item.name}” will be removed from this trip.`}
        confirmLabel="Remove"
        destructive
        pending={deleteItem.isPending}
        onConfirm={onDelete}
      />
    </Card>
  );
}

function TripItemCommentsPanel({
  tripId,
  itemId,
  currentUserId,
}: {
  tripId: string;
  itemId: string;
  currentUserId: string | undefined;
}) {
  const comments = useTripItemComments(tripId, itemId);
  const createComment = useCreateTripItemComment(tripId, itemId);
  const updateComment = useUpdateTripItemComment(tripId, itemId);
  const deleteComment = useDeleteTripItemComment(tripId, itemId);
  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  async function submitNewComment() {
    const body = draft.trim();
    if (!body || createComment.isPending) {
      return;
    }
    try {
      await createComment.mutateAsync({ body });
      setDraft('');
    } catch {
      toast.error('Couldn’t add comment');
    }
  }

  async function addComment(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await submitNewComment();
  }

  function beginEdit(comment: TripItemComment) {
    setEditingId(comment.id);
    setEditDraft(comment.body);
  }

  async function saveEdit(commentId: string) {
    const body = editDraft.trim();
    if (!body || updateComment.isPending) {
      return;
    }
    try {
      await updateComment.mutateAsync({ commentId, body: { body } });
      setEditingId(null);
      setEditDraft('');
    } catch {
      toast.error('Couldn’t edit comment');
    }
  }

  async function removeComment(commentId: string) {
    try {
      await deleteComment.mutateAsync(commentId);
      setConfirmDeleteId(null);
    } catch {
      toast.error('Couldn’t delete comment');
    }
  }

  return (
    <section className="grid gap-3 border-t border-[var(--border)] pt-3">
      <Label htmlFor={`trip-item-comment-${itemId}`}>Comments</Label>
      <form onSubmit={addComment} className="grid gap-2">
        <Textarea
          id={`trip-item-comment-${itemId}`}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (!shouldSubmitCommentWithEnter(event)) {
              return;
            }
            event.preventDefault();
            void submitNewComment();
          }}
          rows={2}
          placeholder="Add a comment"
        />
        <Button
          type="submit"
          size="sm"
          variant="outline"
          className="self-start"
          disabled={createComment.isPending || !draft.trim()}
        >
          <Plus />
          Comment
        </Button>
      </form>
      {comments.isLoading ? (
        <p className="text-sm text-[var(--muted-foreground)]">Loading comments...</p>
      ) : comments.isError ? (
        <p className="text-sm text-[var(--muted-foreground)]">Couldn’t load comments.</p>
      ) : comments.data?.length ? (
        <div className="grid gap-3">
          {comments.data.map((comment) => {
            const mine = comment.createdBy === currentUserId;
            const editing = editingId === comment.id;
            return (
              <div
                key={comment.id}
                className="grid gap-2 rounded-md border border-[var(--border)] bg-[var(--background)] p-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-black dark:text-white">
                      {userDisplayName(comment.createdByUser)}
                    </p>
                    <p className="text-xs text-[var(--muted-foreground)]">
                      {commentTimestamp(comment.createdAt)}
                      {comment.edited ? ' · edited' : ''}
                    </p>
                  </div>
                  {mine && !editing ? (
                    <div className="flex shrink-0 gap-1">
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 rounded-full text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
                        aria-label="Edit comment"
                        onClick={() => beginEdit(comment)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 rounded-full text-[var(--muted-foreground)] hover:text-[var(--destructive)]"
                        aria-label="Delete comment"
                        disabled={deleteComment.isPending}
                        onClick={() => setConfirmDeleteId(comment.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : null}
                </div>
                {editing ? (
                  <div className="grid gap-2">
                    <Textarea
                      value={editDraft}
                      onChange={(event) => setEditDraft(event.target.value)}
                      onKeyDown={(event) => {
                        if (!shouldSubmitCommentWithEnter(event)) {
                          return;
                        }
                        event.preventDefault();
                        void saveEdit(comment.id);
                      }}
                      rows={2}
                      aria-label="Edit comment"
                    />
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={updateComment.isPending || !editDraft.trim()}
                        onClick={() => void saveEdit(comment.id)}
                      >
                        Save
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditingId(null);
                          setEditDraft('');
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-black dark:text-white">
                    {comment.body}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-[var(--muted-foreground)]">No comments yet.</p>
      )}
      <ConfirmDialog
        open={confirmDeleteId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmDeleteId(null);
          }
        }}
        title="Delete this comment?"
        description="This removes the comment from the item for everyone with access."
        confirmLabel="Delete comment"
        destructive
        pending={deleteComment.isPending}
        onConfirm={() => {
          if (confirmDeleteId) {
            void removeComment(confirmDeleteId);
          }
        }}
      />
    </section>
  );
}

function TripItemLessonsPanel({ tripId, item }: { tripId: string; item: TripItemWithLabels }) {
  const [lessonNote, setLessonNote] = useState('');
  const createLesson = useCreateLesson(tripId);

  async function captureLesson(
    type: 'forgot' | 'didnt_need' | 'note',
    text: string,
    clearNote = false,
  ) {
    const trimmed = text.trim();
    if (!trimmed) {
      toast.error('Lesson text is required');
      return;
    }
    try {
      await createLesson.mutateAsync({
        type,
        text: trimmed,
        tripItemId: item.id,
      });
      if (clearNote) {
        setLessonNote('');
      }
      toast.success('Lesson captured');
    } catch {
      toast.error('Couldn’t capture lesson');
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Lightbulb className="h-4 w-4" />
          Lessons
        </CardTitle>
        <CardDescription>Capture what should change next time.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3 p-4 pt-0">
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={createLesson.isPending}
            onClick={() => void captureLesson('forgot', item.name)}
          >
            Forgot this
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={createLesson.isPending}
            onClick={() => void captureLesson('didnt_need', item.name)}
          >
            Didn’t need it
          </Button>
        </div>
        <div className="flex flex-col gap-2">
          <Textarea
            value={lessonNote}
            onChange={(event) => setLessonNote(event.target.value)}
            rows={3}
            placeholder="Capture a note"
            aria-label="Lesson note"
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="self-start"
            disabled={createLesson.isPending || !lessonNote.trim()}
            onClick={() => void captureLesson('note', lessonNote, true)}
          >
            Save note
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(
    new Date(value),
  );
}
